import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import type { AxiosError } from "axios";

const TOO_MANY = "passwordReset.tooMany";
const NO_ACCOUNT = "loginPage.errors.noAccount";
const FAILED = "passwordReset.failed";

/** None is sent to an address without an account here, nor more than a few in a row. In the clientView namespace. */
export const passwordResetErrorKey = (error: unknown): string => {
  const response = (error as AxiosError<{ code?: string }> | undefined)
    ?.response;
  if (response?.status === 429) return TOO_MANY;
  if (response?.data?.code === "RSUSKO026") return NO_ACCOUNT;
  return FAILED;
};

/**
 * What a client page tells about a reset link, in the language of its visitor rather than in the server's English:
 * one toast per message, and a link that leaves takes away the refusal of an earlier try.
 */
export const usePasswordResetToasts = () => {
  const { t } = useTranslation("clientView");
  return useMemo(
    () => ({
      sent: () => {
        [TOO_MANY, NO_ACCOUNT, FAILED].forEach((key) => toast.dismiss(key));
        toast.success(t("passwordReset.sent") as string, {
          toastId: "passwordReset.sent",
        });
      },
      refused: (error: unknown) => {
        const key = passwordResetErrorKey(error);
        toast.error(t(key) as string, { toastId: key });
      },
    }),
    [t]
  );
};
